/**
 * Container for a Tour Stop component.
 */
import React from 'react'
import { connect } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'

import { RootStoreState } from '../../root/index'
import * as collectionActions from '../../collectionlist/actions'
import * as apiActions from '../../api/actions'
import { CollectionItem, Collection, ResponseError } from '../../api/types'
import { getCurrentLanguageCode } from '../../settings/functions'

import { getConfiguration } from '../../configuration/index'
import { GetCollectionsShowResponse, RedeemCode } from 'mytours-read-api-typescript'
import { KeypadEntryStatus, KeypadItem } from '../../scanner/types'
import { CouponCode, CodeStatus } from '../../lockapp/types'
import { isCollectionInRedeemedBundle } from '../../bundles/functions'
import { generateCollectionVersion } from '../../collectionlist/functions'

export interface CollectionItemsContainerOwnProps {
	collectionId: number
	collectionVersion?: number
	collectionAccountId?: number
	accountId?: number
	collectionTitle: string
}

export interface Props extends CollectionItemsContainerOwnProps {
	collectionItems?: CollectionItem[]
	language: string
	hasMap: boolean
	locationEventsEnabled: boolean
	isGuidedMode: boolean
	locationEnabled: boolean
	keypadEnabled: boolean
	collection?: Collection
	keypadEntryStatus?: KeypadEntryStatus
	keypadEntryItems: KeypadItem[]
	keypadEntryLoading: boolean
	lockCollection?: CodeStatus
	error?: ResponseError
	unlockedViaBundle: boolean
}

export interface Actions {
	downloadTour: (version: number, language: string, subtype: 'list' | 'tour' | 'menu', accountId?: number) => void
	loadCollectionDetails: (collectionId: number, collectionVersion: number, language: string, accountId?: number) => void
	validateCode: (couponCode: CouponCode, linkedId: number) => void
}

const mapStateToProps = (state: RootStoreState, ownProps: CollectionItemsContainerOwnProps): Props => {
	// extract params from navigation props
	const containerProps = getConfiguration().convertCollectionItemsContainerOwnProps(ownProps)
	const { collectionId, collectionVersion, collectionTitle, collectionAccountId } = containerProps

	// get details
	const collectionMapValue = state.collectionlist.collectionDetails.get(collectionId)
	//TODO: fix versions
	const collection: Collection | undefined = collectionMapValue?.details
	const hasMap: boolean = collection?.map_view_enabled && collection.map ? true : false
	const isGuidedMode: boolean = collection?.tour_mode === GetCollectionsShowResponse.TourModeEnum.Guided

	// get items
	const collectionItemsMapValue = state.collectionlist.collectionItems.get(collectionId)
	//TODO: fix versions
	const collectionItems: CollectionItem[] | undefined = collectionItemsMapValue?.items

	const language: string = getCurrentLanguageCode(state)
	const locationEnabled: boolean = !!state.settings.appMetaData && state.settings.appMetaData.location_enabled === true
	const keypadEnabled: boolean = !!state.settings.appMetaData && state.settings.appMetaData.keypad_enabled === true

	const manifestItem = state.settings.appManifest?.collections?.find(item => item.id === collectionId)
	const manifestCollectionVersion = manifestItem?.version
	const manifestAccountId = manifestItem?.account_id

	return {
		collectionId,
		collectionVersion: collectionVersion || manifestCollectionVersion || generateCollectionVersion(),
		collectionAccountId: collectionAccountId || manifestAccountId,
		collectionTitle,
		collectionItems,
		language,
		hasMap,
		locationEventsEnabled: state.settings.locationEventsEnabled,
		isGuidedMode,
		locationEnabled,
		keypadEnabled,
		collection,
		keypadEntryStatus: state.scanner.keypadEntryStatus,
		keypadEntryItems: state.scanner.items,
		keypadEntryLoading: state.scanner.loading,
		lockCollection: state.lockapp.collection,
		error: collectionMapValue?.error,
		unlockedViaBundle: isCollectionInRedeemedBundle(collectionId, state.bundles.redeemedBundles, collection),
	}
}

const mapDispatchToProps = <P>(dispatch: Dispatch<AnyAction>, ownProps: P): Actions => {
	// extract params from navigation props
	const containerProps = getConfiguration().convertCollectionItemsContainerOwnProps(ownProps)
	const { collectionId } = containerProps
	return {
		downloadTour: (version: number, language: string, subtype: 'list' | 'tour' | 'menu', accountId?: number) => {
			dispatch(
				collectionActions.loadCollectionItems({
					id: collectionId,
					version,
					language,
					subtype,
					accountId,
				}),
			)
		},
		loadCollectionDetails: (collectionId: number, collectionVersion: number, language: string, accountId?: number) => {
			dispatch(
				collectionActions.loadCollectionDetails({ id: collectionId, version: collectionVersion, language, accountId }),
			)
		},
		validateCode: (couponCode: CouponCode, linkedId: number) => {
			const platform = getConfiguration().getPlatform()
			const deviceUUID = getConfiguration().getDeviceUUID()

			dispatch(
				apiActions.validateCode.started({
					linkedId,
					linkedType: RedeemCode.LinkedTypeEnum.Collection,
					devicePlatform: platform,
					deviceUdid: deviceUUID,
					couponCode: couponCode.code,
					expireAt: couponCode.expireAt,
				}),
			)
		},
	}
}

export default (Component: React.ComponentType<Props & Actions>) =>
	connect<Props, Actions, CollectionItemsContainerOwnProps, RootStoreState>(
		mapStateToProps,
		mapDispatchToProps,
	)(Component)
